import { useState } from "react";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { fmtBalance, fmtRelative, effectiveBalance, lastSyncedAt } from "@/lib/format";
import SyncViaExtensionButton, { useExtensionEligible } from "@/components/SyncViaExtensionButton";
import { useReachable, ReachableInfoButton, PartnersModal } from "@/components/Reachable";

function ExtSyncCell({ p, onSynced }) {
  const eligible = useExtensionEligible(p.code);
  if (!eligible) return null;
  return <SyncViaExtensionButton programCode={p.code} programLabel={p.name} compact onSynced={onSynced} />;
}

// onExtSynced fires whenever the extension finishes posting fresh data for a
// row — it's what pulls the new balance/awards/credits onto the dashboard.

// Map a program's freshness/sync state to a single status pill.
const statusPill = (p) => {
  // If a live-sync account is linked, surface its state; otherwise fall back
  // to the program's freshness field.
  const syncState = p.sync?.state;
  if (syncState) {
    if (syncState === "success" || syncState === "success_with_warning") {
      return ["pill-good", "Live sync"];
    }
    if (syncState === "pending") return ["pill-neutral", "Syncing…"];
    if (syncState === "needs_answer") return ["pill-warn", "Action needed"];
    if (syncState === "invalid_credentials") return ["pill-bad", "Bad credentials"];
    if (syncState === "locked_out") return ["pill-bad", "Locked out"];
    if (syncState === "error" || syncState === "unknown_error" || syncState === "provider_error") {
      return ["pill-bad", "Sync failed"];
    }
    if (syncState === "timeout") return ["pill-bad", "Timed out"];
  }
  // No live-sync — use freshness
  const m = {
    live: ["pill-good", "Live sync"],
    syncing: ["pill-neutral", "Syncing…"],
    sync_error: ["pill-bad", "Sync failed"],
    needs_answer: ["pill-warn", "Action needed"],
    manual: ["pill-neutral", "Manual"],
    stale: ["pill-warn", "Stale"],
  };
  return m[p.freshness] || ["pill-neutral", p.freshness || "—"];
};

const ProgramLogo = ({ p }) => (
  <div
    className="w-12 h-12 rounded-[10px] flex items-center justify-center text-white font-bold text-[14px] shrink-0 overflow-hidden"
    style={{ background: p.color || "#1f2937" }}
  >
    {p.logo ? (
      <img
        src={p.logo}
        alt=""
        className="w-full h-full object-contain bg-white"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    ) : (
      p.name.charAt(0)
    )}
  </div>
);

export default function AccountsTable({
  programs,
  onEdit,
  onAdd,
  onExtSynced,
  onDisconnect,
  onMfa,
}) {
  const { showReachable, reachable } = useReachable();
  const [partnersModal, setPartnersModal] = useState(null);

  return (
    <section data-testid="accounts-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="section-eyebrow">All accounts</div>
          <h2 className="font-display text-xl font-extrabold mt-1 tracking-tight">
            {programs.length} program{programs.length === 1 ? "" : "s"} on file
          </h2>
        </div>
        <button data-testid="add-program-btn" onClick={onAdd} className="btn btn-ghost text-[13px]">
          <Plus size={14} /> Add program
        </button>
      </div>

      <div className="card overflow-hidden">
        <div className="hidden md:grid grid-cols-[44px_1.6fr_1fr_1fr_140px_120px] items-center gap-4 px-5 py-3 border-b border-black/5 text-[11px] uppercase tracking-wider text-neutral-500 font-semibold">
          <div></div>
          <div>Program</div>
          <div className="text-right">Balance</div>
          <div>Status</div>
          <div className="text-right">Source</div>
          <div className="text-right">Actions</div>
        </div>

        {programs.length === 0 ? (
          <div className="px-6 py-14 text-center" data-testid="programs-empty-state">
            <div className="w-12 h-12 rounded-[12px] bg-black/[0.04] flex items-center justify-center mx-auto mb-4">
              <Plus size={20} className="text-neutral-500" />
            </div>
            <div className="font-display font-bold text-[15px] tracking-tight">
              Start tracking your loyalty currencies
            </div>
            <div className="text-[13px] text-neutral-600 mt-1.5 max-w-sm mx-auto leading-relaxed">
              Add your hotel programs, airline miles, and transferable credit card points one at a time. Reservoir keeps balances and tier statuses fresh in the background.
            </div>
            <button
              onClick={onAdd}
              className="btn btn-primary text-[13px] mt-5"
              data-testid="programs-empty-add-btn"
            >
              <Plus size={14} /> Add your first program
            </button>
          </div>
        ) : (
          programs.map((p) => {
            const [pillCls, pillLabel] = statusPill(p);
            const isLinked = !!p.sync?.account_id;
            const reach = reachable[p.id];
            return (
              <div
                key={p.id}
                data-testid={`program-row-${p.code}`}
                className="grid grid-cols-[44px_1.6fr_1fr_1fr_140px_120px] gap-4 items-center px-5 py-3.5 border-b border-black/5 last:border-b-0 hover:bg-black/[0.02] transition-colors"
              >
                <ProgramLogo p={p} />
                <button
                  onClick={() => onEdit(p)}
                  className="text-left min-w-0"
                  data-testid={`program-edit-${p.code}`}
                >
                  <div className="font-semibold text-[14px] truncate flex items-center gap-1.5">
                    <span className="truncate">
                      {p.name}
                      <span className="text-neutral-500 font-normal"> ({p.holder || "self"})</span>
                    </span>
                    {showReachable && (
                      <ReachableInfoButton
                        program={p}
                        reach={reach}
                        onOpenMore={(payload) => setPartnersModal(payload)}
                      />
                    )}
                    {isLinked && (
                      <Lock size={10} className="text-neutral-400 shrink-0" />
                    )}
                  </div>
                  {p.sync?.login && (
                    <div className="text-[12px] text-neutral-500 truncate">
                      {p.sync.login}
                    </div>
                  )}
                </button>
                <button
                  onClick={() => onEdit(p)}
                  className="tabular font-semibold text-[14px] text-right"
                  data-testid={`program-balance-${p.code}`}
                  title={p.pooled_balance > 0 ? `Pooled balance (incl. ${p.balance.toLocaleString()} individual)` : ""}
                >
                  {fmtBalance(effectiveBalance(p), p.currency)}
                </button>
                <div className="text-[13px] text-neutral-600 truncate">{p.tier || "—"}</div>
                <div className="text-right">
                  {p.sync?.state === "needs_answer" || p.freshness === "needs_answer" ? (
                    <button
                      onClick={() => onMfa?.(p)}
                      className={`pill ${pillCls} cursor-pointer hover:opacity-80 transition-opacity`}
                      title="Click to enter the verification code"
                      data-testid={`program-mfa-${p.code}`}
                    >
                      {pillLabel}
                    </button>
                  ) : (
                    <span className={`pill ${pillCls}`} title={p.sync?.error || ""}>
                      {pillLabel}
                    </span>
                  )}
                  {(() => {
                    const ts = lastSyncedAt(p);
                    if (!ts) return null;
                    return (
                      <div
                        className="text-[11px] text-neutral-500 mt-1"
                        title={new Date(ts).toLocaleString()}
                        data-testid={`program-last-sync-${p.code}`}
                      >
                        Synced {fmtRelative(ts)}
                      </div>
                    );
                  })()}
                </div>
                <div className="flex items-center justify-end gap-1.5">
                  {/* AW-linked refresh button is gone now that AwardWallet
                      sync is retired — extension sync covers every program. */}
                  <ExtSyncCell p={p} onSynced={() => onExtSynced?.()} />
                  <button
                    onClick={() => onEdit(p)}
                    title="Edit balance"
                    className="text-neutral-400 hover:text-neutral-700 p-1.5 rounded transition-colors"
                    data-testid={`program-pencil-${p.code}`}
                  >
                    <Pencil size={13} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      <PartnersModal payload={partnersModal} onClose={() => setPartnersModal(null)} />
    </section>
  );
}
